/**
 * Which fix the admin has picked for each breach, and how picks interact.
 *
 * Two picks that both rearrange the same guard's day are not combined: each was
 * checked against the roster as it stands, not against the other, and stacking
 * them could double-book someone in a way no rule catches. Picking one clears
 * the other; whatever is left is fixed on a second pass once the first lands.
 */

import { clearsViolation } from './verification';
import type { ComplianceIssue, FixOption, RosterChangeOperation } from './types';

/** Issue key → chosen option id, or null to leave that breach as it is. */
export type FixSelection = Record<string, string | null>;

export function involvedGuardIds(option: FixOption): Set<string> {
  const ids = new Set<string>();
  option.operations.forEach((operation) => {
    switch (operation.op) {
      case 'reassign_duty':
      case 'transfer_break':
        ids.add(operation.from_guard_id);
        ids.add(operation.to_guard_id);
        break;
      case 'split_duty':
        ids.add(operation.guard_id);
        if (operation.handover_guard_id) ids.add(operation.handover_guard_id);
        break;
      case 'remove_duty':
      case 'add_break':
      case 'remove_break':
        ids.add(operation.guard_id);
        break;
    }
  });
  return ids;
}

export function optionsConflict(a: FixOption, b: FixOption): boolean {
  const guards = involvedGuardIds(a);
  return Array.from(involvedGuardIds(b)).some((id) => guards.has(id));
}

export function findOption(issues: ComplianceIssue[], optionId: string | null | undefined): FixOption | null {
  if (!optionId) return null;
  for (const issue of issues) {
    const option = issue.options.find((candidate) => candidate.id === optionId);
    if (option) return option;
  }
  return null;
}

/** The picked fixes in issue order. */
export function selectedFixes(issues: ComplianceIssue[], selection: FixSelection): Array<{ issue: ComplianceIssue; option: FixOption }> {
  return issues.flatMap((issue) => {
    const option = findOption([issue], selection[issue.key]);
    return option ? [{ issue, option }] : [];
  });
}

/**
 * Breaches another pick already clears, mapped to the issue whose pick does it.
 * Those need no fix of their own.
 */
export function coveredIssues(issues: ComplianceIssue[], selection: FixSelection): Map<string, string> {
  const covered = new Map<string, string>();
  const chosen = selectedFixes(issues, selection);
  issues.forEach((issue) => {
    if (selection[issue.key]) return;
    const coverer = chosen.find(({ option }) => clearsViolation(option.verification, issue.violation));
    if (coverer) covered.set(issue.key, coverer.issue.key);
  });
  return covered;
}

/**
 * The recommended fix for each breach, skipping any a previous pick covers or
 * would clash with. Fixes that clear the most breaches are picked first, so one
 * reassignment that cures a guard's rest and break together wins over patching
 * the break and leaving the rest for a second pass.
 */
export function defaultSelection(issues: ComplianceIssue[]): FixSelection {
  const selection: FixSelection = {};
  const picked: FixOption[] = [];

  const recommendations = issues.map((issue, index) => {
    selection[issue.key] = null;
    return { issue, index, recommended: findOption([issue], issue.recommendedOptionId) };
  });

  recommendations
    .sort((a, b) => (
      (b.recommended?.verification.resolved.length ?? 0) - (a.recommended?.verification.resolved.length ?? 0)
      || a.index - b.index
    ))
    .forEach(({ issue, recommended }) => {
      if (!recommended) return;
      if (picked.some((option) => clearsViolation(option.verification, issue.violation))) return;
      if (picked.some((option) => optionsConflict(option, recommended))) return;
      selection[issue.key] = recommended.id;
      picked.push(recommended);
    });

  return selection;
}

export interface SelectionChange {
  selection: FixSelection;
  /** Issues whose pick was cleared because it touched the same guards. */
  cleared: string[];
}

export function chooseOption(
  issues: ComplianceIssue[],
  selection: FixSelection,
  issueKey: string,
  optionId: string | null,
): SelectionChange {
  const next: FixSelection = { ...selection, [issueKey]: optionId };
  const option = findOption(issues, optionId);
  const cleared: string[] = [];

  if (option) {
    issues.forEach((issue) => {
      if (issue.key === issueKey) return;
      const other = findOption([issue], next[issue.key]);
      if (other && optionsConflict(option, other)) {
        next[issue.key] = null;
        cleared.push(issue.key);
      }
    });
  }

  return { selection: next, cleared };
}

const operationKey = (operation: RosterChangeOperation): string => JSON.stringify(
  Object.keys(operation).sort().map((key) => [key, (operation as unknown as Record<string, unknown>)[key]]),
);

/** Every chosen operation in issue order, with exact repeats dropped. */
export function selectedOperations(issues: ComplianceIssue[], selection: FixSelection): RosterChangeOperation[] {
  const seen = new Set<string>();
  return selectedFixes(issues, selection)
    .flatMap(({ option }) => option.operations)
    .filter((operation) => {
      const key = operationKey(operation);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}
